/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 */

import React, { createContext, useContext, useState } from 'react';
import type { AuthUser } from '../types';
import api from '../services/api';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** Adopt an already-authenticated user (e.g. after the new-driver wizard creates the account). */
  applyAuth: (user: AuthUser) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => api.getStoredUser());

  const signIn = async (email: string, password: string) => {
    const u = await api.signIn(email, password);
    setUser(u);
  };

  const applyAuth = (u: AuthUser) => setUser(u);

  const signOut = () => {
    api.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, signIn, applyAuth, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
